import { useEffect, useState } from 'react'
import { apiService, type TenantProvisioningSummary } from '@/service/api'
import { toast } from 'sonner'
import { Loader2, Save, ChevronDown, ChevronUp } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/i18n'

export default function PlatformAgentProvisioning() {
  const { t } = useTranslation()
  const [allAgents, setAllAgents] = useState<{ id: string; name: string }[]>([])
  const [tenants, setTenants] = useState<TenantProvisioningSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedTenant, setExpandedTenant] = useState<string | null>(null)
  const [pendingChanges, setPendingChanges] = useState<
    Record<string, string[]>
  >({})
  const [saving, setSaving] = useState<string | null>(null)

  useEffect(() => {
    loadTenants()
  }, [])

  const loadTenants = async () => {
    try {
      setLoading(true)
      const [agents, data] = await Promise.all([
        apiService.listAvailableAgents(),
        apiService.listTenantProvisioning(),
      ])
      setAllAgents(agents)
      setTenants(data)
    } catch (err) {
      console.error('Failed to load tenant provisioning:', err)
      toast.error(t('common:settings.platform_provisioning.load_error'))
    } finally {
      setLoading(false)
    }
  }

  const getProvisionedAgents = (tenantId: string): string[] => {
    if (pendingChanges[tenantId]) return pendingChanges[tenantId]
    const tenant = tenants.find((tn) => tn.tenantId === tenantId)
    return tenant?.provisionedAgents ?? allAgents.map((a) => a.id)
  }

  const toggleAgent = (tenantId: string, agentId: string) => {
    const current = getProvisionedAgents(tenantId)
    const updated = current.includes(agentId)
      ? current.filter((id) => id !== agentId)
      : [...current, agentId]
    setPendingChanges((prev) => ({ ...prev, [tenantId]: updated }))
  }

  const hasPendingChanges = (tenantId: string): boolean => {
    if (!pendingChanges[tenantId]) return false
    const tenant = tenants.find((tn) => tn.tenantId === tenantId)
    const original = tenant?.provisionedAgents ?? allAgents.map((a) => a.id)
    const pending = pendingChanges[tenantId]
    return (
      original.length !== pending.length ||
      !original.every((id) => pending.includes(id))
    )
  }

  const handleSave = async (tenantId: string) => {
    const agents = getProvisionedAgents(tenantId)
    setSaving(tenantId)
    try {
      const updated = await apiService.updateTenantProvisioning(
        tenantId,
        agents
      )
      setTenants((prev) =>
        prev.map((tn) => (tn.tenantId === tenantId ? updated : tn))
      )
      setPendingChanges((prev) => {
        const next = { ...prev }
        delete next[tenantId]
        return next
      })
      toast.success(t('common:settings.platform_provisioning.save_success'))
    } catch (err) {
      console.error('Failed to update provisioning:', err)
      toast.error(t('common:settings.platform_provisioning.save_error'))
    } finally {
      setSaving(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" />
      </div>
    )
  }

  if (tenants.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-dark-text-muted">
        {t('common:settings.platform_provisioning.no_tenants')}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {tenants.map((tenant) => {
        const isExpanded = expandedTenant === tenant.tenantId
        const provisioned = getProvisionedAgents(tenant.tenantId)
        const hasChanges = hasPendingChanges(tenant.tenantId)
        const isSaving = saving === tenant.tenantId

        return (
          <div
            key={tenant.tenantId}
            className="border border-gray-200 dark:border-dark-border rounded-lg bg-white dark:bg-dark-card"
          >
            {/* Tenant header row */}
            <button
              className="w-full flex items-center justify-between px-5 py-4 text-start hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors"
              onClick={() =>
                setExpandedTenant(isExpanded ? null : tenant.tenantId)
              }
            >
              <div className="flex items-center gap-3 min-w-0">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-dark-text">
                    {tenant.displayName || tenant.tenantName}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-0.5">
                    {/* Show the Keycloak slug alongside the id when it differs
                        from the display-name heading (new slug-shaped orgs);
                        legacy rows where name === displayName show just the id. */}
                    {tenant.displayName && tenant.displayName !== tenant.tenantName
                      ? `${tenant.tenantName} · ${tenant.tenantId}`
                      : tenant.tenantId}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <Badge
                  variant="outline"
                  className="text-xs"
                >
                  {provisioned.length}/{allAgents.length} {t('common:settings.platform_provisioning.agents_count')}
                </Badge>
                {hasChanges && (
                  <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/30 text-xs">
                    {t('common:settings.platform_provisioning.unsaved')}
                  </Badge>
                )}
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                )}
              </div>
            </button>

            {/* Expanded agent list */}
            {isExpanded && (
              <div className="px-5 pb-4 border-t border-gray-100 dark:border-dark-border">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  {allAgents.map((agent) => {
                    const isProvisioned = provisioned.includes(agent.id)
                    const isEnabled =
                      tenant.enabledAgents?.includes(agent.id) ?? false

                    return (
                      <label
                        key={agent.id}
                        className="flex items-center gap-3 p-3 rounded-md border border-gray-100 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-muted cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={isProvisioned}
                          onCheckedChange={() =>
                            toggleAgent(tenant.tenantId, agent.id)
                          }
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-900 dark:text-dark-text">
                            {agent.name}
                          </span>
                        </div>
                        {isProvisioned && isEnabled && (
                          <Badge
                            variant="outline"
                            className="text-xs text-green-600 dark:text-green-400 border-green-200 dark:border-green-800"
                          >
                            {t('common:settings.platform_provisioning.enabled')}
                          </Badge>
                        )}
                        {isProvisioned && !isEnabled && (
                          <Badge
                            variant="outline"
                            className="text-xs text-gray-400 dark:text-gray-500 border-gray-200 dark:border-dark-border"
                          >
                            {t('common:settings.platform_provisioning.disabled')}
                          </Badge>
                        )}
                      </label>
                    )
                  })}
                </div>

                {hasChanges && (
                  <div className="flex justify-end mt-4">
                    <Button
                      size="sm"
                      onClick={() => handleSave(tenant.tenantId)}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin me-2" />
                      ) : (
                        <Save className="h-4 w-4 me-2" />
                      )}
                      {t('common:settings.platform_provisioning.save_changes')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
